import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import add from 'editor/helpers/add';
import eq from 'editor/helpers/eq';
import htmlId from 'editor/helpers/html-id';
import notEq from 'editor/helpers/not-eq';
import or from 'editor/helpers/or';
import queue from 'editor/helpers/queue';
import {
  DeleteSongFolderCommand,
  MoveSongCommand,
  RenameFolderCommand,
  SortSongsCommand,
} from 'editor/models/folder-commands';
import {
  AddPlaylistCommand,
  AddSongToPlaylistCommand,
} from 'editor/models/playlist-commands';
import {
  DeleteSongCommand,
  DuplicateSongCommand,
  RenameSongCommand,
} from 'editor/models/song-commands';
import onClickOutside from 'editor/modifiers/on-click-outside';
import scrollIntoView from 'editor/modifiers/scroll-into-view';
import selected from 'editor/modifiers/selected';
import sortable from 'editor/modifiers/sortable';
import ProjectManagerService, {
  Playlist,
  SongFile,
  SongFolder,
} from 'editor/services/project-manager';
import UndoManagerService from 'editor/services/undo-manager';
import nameFilter from 'editor/utils/name-filter';
import { modifier } from 'ember-modifier';
import velcro from 'ember-velcro/modifiers/velcro';
import Button from './button';
import MenuButton from './menu-button';
import OptionButton from './option-button';
import SelectableItem from './selectable-item';


interface FolderSignature {
  Element: HTMLDivElement;
  Args: {
    folder: SongFolder;
    isSelected: boolean;
    selectedSong?: SongFile;
    folderIndex: number;
    filter: string;
    addSong: () => void;
    importSong: () => void;
    onMoveSong: (
      // eslint-disable-next-line no-unused-vars
      fromElement: HTMLElement,
      // eslint-disable-next-line no-unused-vars
      fromIndex: number,
      // eslint-disable-next-line no-unused-vars
      toElement: HTMLElement,
      // eslint-disable-next-line no-unused-vars
      toIndex: number,
    ) => void;
    // eslint-disable-next-line no-unused-vars
    onSelectSong: (folder: SongFolder, song: SongFile) => void;
  };
  Blocks: {
    // default: [];
  };
}

const ensureInView = modifier((element: HTMLElement) => {
  var intersectionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.target == element && entry.isIntersecting) {
        const rect = element.getBoundingClientRect();
        if (rect.height + rect.top > window.innerHeight) {
          element.style.top = `-${rect.top - 20}px`;
          element.style.maxHeight = `${window.innerHeight - 40}px`;
        }
      }
    });
  });

  intersectionObserver.observe(element);
});

export default class FolderComponent extends Component<FolderSignature> {
  @service declare projectManager: ProjectManagerService;
  @service declare undoManager: UndoManagerService;
  @service declare router: RouterService;

  @tracked _isOpen?: boolean;
  @tracked menuSong?: SongFile;
  @tracked menuFolder?: SongFolder;
  @tracked ignoreNextClickOutside: boolean = false;

  constructor(owner: unknown, args: FolderSignature['Args']) {
    super(owner, args);
    this._isOpen = args.isSelected;
    this.projectManager.on('folderChange', (folder) => {
      if (folder === this.folder) {
        this._isOpen = true;
      }
    });
  }

  get isOpen(): boolean {
    if (this.args.filter !== '' && this.matchesFilter) {
      return true;
    }
    return !!(
      this._isOpen ??
      (this.folder === this.projectManager.currentFolder &&
        this.projectManager.currentSong &&
        this.folder.songs.includes(this.projectManager.currentSong))
    );
  }

  get matchesFilter() {
    return (
      this.args.filter === '' || this.folderMatchesFilter || this.filteredSongs.length > 0
    );
  }

  get folderMatchesFilter() {
    return this.folder.name
      .toLocaleLowerCase()
      .includes(this.args.filter.toLocaleLowerCase());
  }

  @action
  songMatchesFilter(song: SongFile) {
    return this.filteredSongs.includes(song);
  }

  get filteredSongs() {
    if (this.args.filter === '') {
      return this.folder.songs;
    }
    return this.folder.songs.filter((song) =>
      song.name.toLocaleLowerCase().includes(this.args.filter.toLocaleLowerCase()),
    );
  }

  get visibleSongs(): (SongFile | DrumsetFile)[] {
    const filter = this.args.filter?.trim().toLowerCase() ?? '';

    if (!filter) {
      // No filter: all songs are visible
      return this.folder.songs;
    }

    if (this.folderMatchesFilter) {
      // Folder name matches filter -> all songs are visible
      return this.folder.songs;
    }

    // Otherwise, only songs that match
    return this.folder.songs.filter((song) =>
      song.name.toLowerCase().includes(filter),
    );
  }


  get folder(): SongFolder {
    return this.args.folder;
  }

  get isCurrent() {
    return this.folder == this.projectManager.currentFolder;
  }

  @action
  toggleOpenClose() {
    this._isOpen = !this.isOpen;
  }

  @action
  renameFolder(folder: SongFolder, name: string) {
    if (name === folder.name) {
      return;
    }
    this.undoManager.executeCommand(new RenameFolderCommand(folder, name));
  }

  @action
  renameSong(song: SongFile, name: string) {
    if (name === song.name) {
      return;
    }
    this.undoManager.executeCommand(new RenameSongCommand(song.song!, name));
  }

  @action
  deleteSong(song: SongFile) {
    const command = new DeleteSongCommand(this.folder, song);
    command.on('execute', () => {
      this.router.transitionTo('restricted.songs.folder');
    });
    command.on('reverse', (song) => {
      this.router.transitionTo('restricted.songs.folder.song', song.path);
    });
    this.undoManager.executeCommand(command);
  }

  @action
  duplicateSong(song: SongFile) {
    const command = new DuplicateSongCommand(this.projectManager, this.folder, song);
    const folder = this.folder;
    command.on('execute', (newSong) => {
      this.router.transitionTo('restricted.songs.folder.song', folder, newSong.path);
    });
    command.on('reverse', (song) => {
      this.router.transitionTo('restricted.songs.folder.song', folder, song.path);
    });
    this.undoManager.executeCommand(command);
  }

  @action
  sortSongs(folder: SongFolder, oldIndex: number, newIndex: number) {
    // eslint-disable-next-line ember/no-runloop
    next(() => {
      const visible = this.visibleSongs;

      const movedSong = visible[oldIndex];
      if (!movedSong) {
        return;
      }

      const realFromIndex = folder.songs.indexOf(movedSong);
      if (realFromIndex === -1) {
        return;
      }

      let realToIndex: number;

      if (newIndex >= visible.length) {
        // Dropped after the last visible song
        const lastVisible = visible[visible.length - 1];
        const lastVisibleIndex = folder.songs.indexOf(lastVisible);
        realToIndex = lastVisibleIndex + 1;
      } else {
        // Insert before the song currently at newIndex in the visible list
        const beforeSong = visible[newIndex];
        realToIndex = folder.songs.indexOf(beforeSong);
        if (realToIndex === -1) {
          realToIndex = folder.songs.length;
        }
      }

      this.undoManager.executeCommand(
        new SortSongsCommand(folder, realFromIndex, realToIndex),
      );
    });
  }

  @action
  moveSong(song: SongFile, toFolder: SongFolder) {
    const fromIndex = this.folder.songs.indexOf(song);
    const command = new MoveSongCommand(this.folder, fromIndex, toFolder);
    command.on('execute', (newSong) => {
      this.router.transitionTo(
        'restricted.songs.folder.song',
        toFolder.path,
        newSong.path,
      );
    });
    command.on('reverse', (song) => {
      this.router.transitionTo(
        'restricted.songs.folder.song',
        song.folder.path,
        song.path,
      );
    });
    this.undoManager.executeCommand(command);
  }

  @action
  addToPlaylist(playlist: Playlist, song: SongFile) {
    const command = new AddSongToPlaylistCommand(playlist, song);
    this.undoManager.executeCommand(command);
  }

  @action
  async addToNewPlaylist(song: SongFile) {
    const playlist = await this.undoManager.executeCommand(
      new AddPlaylistCommand(this.projectManager),
    );
    this.undoManager.appendCommand(new AddSongToPlaylistCommand(playlist, song));
  }

  @action
  async exportSong(song: SongFile) {
    await this.projectManager.exportSong(song);
  }

  @action
  async exportAllMidiFiles(song: SongFile) {
    await this.projectManager.exportAllMidiFiles(song);
  }

  @action
  showMenuFor(item: SongFile | SongFolder | undefined, ev?: MouseEvent) {
    if (!item && this.ignoreNextClickOutside) {
      this.ignoreNextClickOutside = false;
      return;
    }
    if (item && this.menuSong != item && this.menuFolder != item) {
      ev?.preventDefault();
      ev?.stopPropagation();
      if (this.menuSong || this.menuFolder) {
        this.ignoreNextClickOutside = true;
      }
    }
    if (!item) {
      this.menuSong = undefined;
      this.menuFolder = undefined;
      return;
    }
    if (item instanceof SongFile) {
      this.menuSong = item;
      this.menuFolder = undefined;
    } else {
      this.menuFolder = item;
      this.menuSong = undefined;
    }
  }

  @action
  shouldShowMenuFor(item: SongFile | SongFolder) {
    return this.menuSong === item || this.menuFolder === item;
  }

  @action
  deleteFolder(folder: SongFolder) {
    const command = new DeleteSongFolderCommand(this.projectManager, folder);
    command.on('execute', () => {
      this.router.transitionTo('restricted.songs');
    });
    command.on('reverse', (folder) => {
      this.router.transitionTo('restricted.songs.folder', folder.path);
    });
    this.undoManager.executeCommand(command);
  }

  @action
  exportFolder(folder: SongFolder) {
    this.projectManager.exportFolder(folder);
  }

  <template>
    <div class="bg-white dark:bg-primary-darkComponent rounded {{unless this.matchesFilter 'hidden'}}" ...attributes>
      {{! template-lint-disable no-invalid-interactive}}
      <div class="flex items-center justify-between group/folder dark:selected:bg-primary-darkButtonSelected dark:selected:text-black">
        <SelectableItem
          @icon={{if this.isOpen "folder-open" "folder"}}
          @model={{this.folder}}
          @field="name"
        {{!--  @filter={{nameFilter}} --}}
          @when={{this.isOpen}}
          @onChange={{fn this.renameFolder this.folder}}
          title={{this.folder.name}}
          class="folder-name bg-white"
          {{selected when=this.isOpen}}
          {{on "click" this.toggleOpenClose}}
          {{scrollIntoView when=this.isOpen}}
        >{{add @folderIndex 1}}. {{this.folder.name}}</SelectableItem>
        <OptionButton
          id={{concat "folder-menu-" this.folder.id}}
          class="invisible text-black group-hover/folder:visible selected:visible dark:text-white dark:group-hover/folder:text-white dark:selected:group-hover/folder:text-white"
          {{selected when=this.isOpen}}
          {{on "click" (fn this.showMenuFor this.folder)}}
        />
        {{#if (this.shouldShowMenuFor this.folder)}}
          <nav
            aria-label="Folder menu"
            class="absolute z-50 flex flex-col bg-white border rounded shadow-lg dark:bg-primary-darkComponent"
            {{velcro
              (htmlId (concat "folder-menu-" this.folder.id))
              placement="left-start"
            }}
            {{onClickOutside (fn this.showMenuFor undefined)}}
          >
            <MenuButton
              @icon="download"
              {{on
                "click"
                (queue (fn this.exportFolder this.folder) (fn this.showMenuFor undefined))
              }}
              title="Export folder"
            >Export</MenuButton>
            <MenuButton
              @icon="trash-can"
              title="Delete folder"
              {{on
                "click"
                (queue (fn this.deleteFolder this.folder) (fn this.showMenuFor undefined))
              }}
            >Delete</MenuButton>
          </nav>
        {{/if}}
      </div>
      <div
        data-folder-index={{@folderIndex}}
        class={{if this.isOpen "block pl-4" "hidden"}}
        {{sortable
          draggable=".song-file"
          group="songs"
          swapThreshold=0.65
          removeItem=true
          onChange=(fn this.sortSongs this.folder)
          onMove=@onMoveSong
        }}
      >
        {{~#if this.isOpen~}}
          {{~#each this.folder.songs as |song songIndex|~}}
            {{#if (or this.folderMatchesFilter (this.songMatchesFilter song))}}
              <div
                class="flex items-center gap-2 overflow-hidden rounded song-file selected:bg-primary selected:text-white group/options dark:selected:bg-primary-darkButtonSelected dark:selected:text-black"
                {{selected when=(eq @selectedSong song)}}
                {{on "click" (fn @onSelectSong this.folder song)}}
              >
                <SelectableItem
                  @icon="music"
                  @model={{song}}
                  @field="name"
                {{!--  @filter={{nameFilter}} --}}
                  @when={{eq @selectedSong song}}
                  @onChange={{fn this.renameSong song}}
                  title={{song.name}}
                  {{selected when=(eq @selectedSong song)}}
                >{{add songIndex 1}}. {{song.name}}</SelectableItem>

                <div
                  class="relative invisible text-black group-hover/options:visible selected:visible"
                  {{selected when=(eq @selectedSong song)}}
                >
                  <OptionButton
                    id={{concat "song-menu-" song.id}}
                    class="text-white group-hover/options:text-black selected:group-hover/options:text-white dark:text-black dark:group-hover/options:text-white dark:selected:group-hover/options:text-black"
                    {{selected when=(eq @selectedSong song)}}
                    {{on "click" (fn this.showMenuFor song)}}
                  />
                  {{#if (this.shouldShowMenuFor song)}}
                    <nav
                      aria-label="Song menu"
                      class="fixed z-50 flex flex-col bg-white border rounded shadow-lg dark:bg-primary-darkComponent dark:border-primary-darkBorderColor"
                      {{velcro
                        (htmlId (concat "song-menu-" song.id))
                        placement="left-start"
                      }}
                      {{onClickOutside (fn this.showMenuFor undefined)}}
                    >
                      <div class="relative group/move">
                        <MenuButton @icon="folder-arrow-down">Move to…</MenuButton>
                        <div
                          class="absolute top-0 z-50 flex flex-col invisible overflow-auto bg-white border rounded shadow-lg left-full min-w-max group-hover/move:visible"
                          {{ensureInView}}
                        >
                          {{#each this.projectManager.songFolders as |folder|}}
                            {{#if (notEq folder this.folder)}}
                              <MenuButton
                                @icon="folder"
                                {{on
                                  "click"
                                  (queue
                                    (fn this.moveSong song folder)
                                    (fn this.showMenuFor undefined)
                                  )
                                }}
                              >
                                {{folder.name}}
                              </MenuButton>
                            {{/if}}
                          {{/each}}
                        </div>
                      </div>
                      <div class="relative group/playlist">
                        <MenuButton @icon="folder-music">Add to…</MenuButton>
                        <div
                          class="absolute top-0 z-50 flex flex-col invisible overflow-auto bg-white border rounded shadow-lg left-full min-w-max group-hover/playlist:visible"
                          {{ensureInView}}
                        >
                          {{#each this.projectManager.playlists as |playlist|}}
                            <MenuButton
                              @icon="list-music"
                              {{on
                                "click"
                                (queue
                                  (fn this.addToPlaylist playlist song)
                                  (fn this.showMenuFor undefined)
                                )
                              }}
                            >
                              {{playlist.name}}
                            </MenuButton>
                          {{/each}}
                          {{#if this.projectManager.canAddPlaylist}}
                            <MenuButton
                              @icon="list-music"
                              {{on
                                "click"
                                (queue
                                  (fn this.addToNewPlaylist song)
                                  (fn this.showMenuFor undefined)
                                )
                              }}
                            >
                              New Playlist
                            </MenuButton>
                          {{/if}}
                        </div>
                      </div>
                      <MenuButton
                        @icon="clone"
                        title="Duplicate song"
                        {{on
                          "click"
                          (queue
                            (fn this.duplicateSong song) (fn this.showMenuFor undefined)
                          )
                        }}
                      >Duplicate</MenuButton>
                      <MenuButton
                        @icon="download"
                        {{on
                          "click"
                          (queue
                            (fn this.exportSong song) (fn this.showMenuFor undefined)
                          )
                        }}
                        title="Export song"
                      >Export</MenuButton>
                      <MenuButton
                        @icon="download"
                        {{on
                          "click"
                          (queue
                            (fn this.exportAllMidiFiles song) (fn this.showMenuFor undefined)
                          )
                        }}
                        title="Export all midi files"
                      >Export MIDI</MenuButton>
                      <MenuButton
                        @icon="trash-can"
                        title="Delete song"
                        {{on
                          "click"
                          (queue
                            (fn this.deleteSong song) (fn this.showMenuFor undefined)
                          )
                        }}
                      >Delete</MenuButton>
                    </nav>
                  {{/if}}
                </div>
              </div>
            {{/if}}
          {{~else~}}
            <Button @icon="music" class="ml-3" {{on "click" @addSong}}>Create Song</Button>
            <Button @icon="music" class="ml-3" {{on "click" @importSong}}>Import Song</Button>
          {{~/each~}}
        {{~/if~}}
      </div>
    </div>
  </template>
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    Folder: typeof FolderComponent;
  }
}
